<template>
    <component
        :is="tag"
        type="button"
        :to="to"
        :href="url"
        :class="classObject"
        :disabled="isLoading || isDisabled"
        data-spec-class="button"
        @click="onRootClick"
    >
        <slot name="prepend" />
        <span class="flex items-center px-4">
            <span
                class="transition-ease"
                :class="spinnerWrapperClassObject"
            >
                <BaseSpinner
                    :is-active="isLoading"
                    :size="size"
                    :class="spinnerClassObject"
                />
            </span>
            <slot />
        </span>
        <BaseBadge
            v-if="isNewBadgeVisible"
            :theme="BadgeTheme.Cta"
            :size="badgeSize"
            is-floating
            data-spec-class="button-new-badge"
        >
            {{ t('general.new') }}
        </BaseBadge>
    </component>
</template>

<script lang="ts" setup>
import { computed, withDefaults, defineProps, defineEmits, defineExpose } from 'vue';
import { RouteLocationNormalized, RouterLink, useRouter } from 'vue-router';
import { BadgeSize, BadgeTheme, SizeAll, Theme } from '@/types';
import BaseSpinner from './BaseSpinner.vue';
import BaseBadge from './BaseBadge.vue';
import { useI18n } from 'vue-i18n';

const router = useRouter();
const { t } = useI18n();

const emits = defineEmits<{
    click: [event: MouseEvent];
}>();

interface Props {
    theme?: Theme;
    href?: string;
    to?: RouteLocationNormalized;
    isDisabled?: boolean;
    size?: SizeAll;
    isLoading?: boolean;
    newBadgeUntil?: Date;
}

const props = withDefaults(defineProps<Props>(), {
    theme: Theme.Normal,
    href: undefined,
    to: undefined,
    isDisabled: false,
    size: SizeAll.Normal,
    isLoading: false,
    newBadgeUntil: undefined,
});

const isNewBadgeVisible = computed(() => {
    if (!props.newBadgeUntil) {
        return false;
    }
    return Date.now() < props.newBadgeUntil.getTime();
});

const sizeAllToBadgeSizeMap: Record<SizeAll, BadgeSize> = {
    [SizeAll.Tiny]: BadgeSize.Small,
    [SizeAll.Small]: BadgeSize.Small,
    [SizeAll.Normal]: BadgeSize.Normal,
    [SizeAll.Large]: BadgeSize.Large,
    [SizeAll.Huge]: BadgeSize.Large,
};

const badgeSize = computed(() => sizeAllToBadgeSizeMap[props.size]);

const spinnerWrapperClassObject = computed(() => {
    return {
        'mr-1': props.isLoading,
        'mr-0 w-0': !props.isLoading,
        'w-5': props.isLoading && (props.size === SizeAll.Tiny || props.size === SizeAll.Small),
        'w-6': props.isLoading && props.size === SizeAll.Normal,
        'w-7': props.isLoading && (props.size === SizeAll.Large || props.size === SizeAll.Huge),
    };
});

const spinnerClassObject = computed(() => {
    if (props.theme === Theme.Cta) return 'text-white';
    if (props.theme === Theme.Primary) return 'text-white';
    return 'text-tertiary';
});

const tag = computed(() => {
    if (props.to) {
        return RouterLink;
    }
    if (props.href) {
        return 'a';
    }

    return 'button';
});

const url = computed(() => {
    if (props.to) {
        return router.resolve(props.to).fullPath;
    }
    if (props.href) {
        return props.href;
    }

    return '#';
});

const classObject = computed(() => ({
    'relative inline-flex justify-center items-center font-medium transition-ease rounded-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed': true,
    'py-1 px-0 text-xs': props.size === SizeAll.Tiny,
    'py-2 px-2.5 text-xs': props.size === SizeAll.Small,
    'py-2 px-2.5 text-sm': props.size === SizeAll.Normal,

    // outlined
    'bg-none text-brand border border-brand hover:bg-brand hover:text-gray-100':
        props.theme === Theme.Normal,
    'disabled:hover:!bg-transparent disabled:hover:!text-brand': props.theme === Theme.Normal,

    'bg-none text-secondary border border-secondary hover:bg-secondary hover:text-primary--inverse':
        props.theme === Theme.Secondary,
    'disabled:hover:!bg-transparent disabled:hover:!text-secondary':
        props.theme === Theme.Secondary,

    'bg-none text-danger border border-danger hover:bg-danger hover:text-gray-100':
        props.theme === Theme.Danger,
    'disabled:hover:!bg-transparent disabled:hover:!text-red': props.theme === Theme.Danger,

    // filled
    'bg-blue-800 hover:bg-transparent text-gray-100 hover:text-brand border border-brand':
        props.theme === Theme.Primary,
    'disabled:hover:!bg-brand disabled:hover:!text-gray-100': props.theme === Theme.Primary,

    'bg-cta hover:bg-transparent text-gray-100 hover:text-cta border border-cta':
        props.theme === Theme.Cta,
    'disabled:hover:!bg-cta disabled:hover:!text-gray-100': props.theme === Theme.Cta,
}));

function onRootClick(event: MouseEvent): void {
    emits('click', event);
}

function remoteTrigger(): void {
    emits('click', new MouseEvent('click', { bubbles: true, cancelable: true }));
}

defineExpose({ remoteTrigger });
</script>
